export type HouseId = "forecast" | "spot" | "season";
export type LocationTabId = HouseId | "map";

export const HOUSES: { id: HouseId; label: string; description: string }[] = [
  { id: "forecast", label: "Forecast", description: "Today & week" },
  { id: "spot", label: "Spot guide", description: "Access & local info" },
  { id: "season", label: "Season", description: "History & timing" },
];

export const DEFAULT_HOUSE: HouseId = "forecast";

export function locationTabs(withMap = false): { id: LocationTabId; label: string; description: string }[] {
  return withMap
    ? [HOUSES[0], { id: "map", label: "Map & routes", description: "Explore & plan" }, ...HOUSES.slice(1)]
    : HOUSES;
}

export function resolveLocationTab(param: string | null, withMap: boolean): LocationTabId {
  return withMap && param === "map" ? "map" : resolveHouse(param);
}

/** Stable information architecture shared by coastal and mountain location pages. */
export const LOCATION_PAGE_SLOT_ORDER = {
  aboveTabs: ["current-score", "safety", "factor-summary", "disclaimer"],
  forecast: ["today-hourly", "outlook", "tactical", "factor-explanation", "planner", "packing"],
  spot: ["description", "local-detail", "nearby", "community", "environment"],
  season: ["season-pattern", "recent-form", "accuracy", "additional"],
} as const;

export type LocationPageRegion = keyof typeof LOCATION_PAGE_SLOT_ORDER;
export type LocationHouseSlot<R extends LocationPageRegion> =
  (typeof LOCATION_PAGE_SLOT_ORDER)[R][number];
export type LocationHouseSlots<R extends LocationPageRegion, T> = Partial<
  Record<LocationHouseSlot<R>, T | null | undefined>
>;
export type OrderedLocationHouseSlot<R extends LocationPageRegion, T> = {
  slot: LocationHouseSlot<R>;
  value: T;
};

/** Compose named location-page content in the canonical production order. */
export function orderLocationHouseSlots<R extends LocationPageRegion, T>(
  region: R,
  slots: LocationHouseSlots<R, T>,
): OrderedLocationHouseSlot<R, T>[] {
  const slotValues = slots as Partial<Record<string, T | null | undefined>>;
  return LOCATION_PAGE_SLOT_ORDER[region]
    .flatMap((slot) => {
      const value = slotValues[slot];
      return value === null || value === undefined
        ? []
        : [{ slot, value } as OrderedLocationHouseSlot<R, T>];
    });
}

/** Resolve a ?tab= value to a valid house id (defaults to "forecast"). */
export function resolveHouse(param: string | null | undefined): HouseId {
  if (param && HOUSES.some((h) => h.id === param)) return param as HouseId;
  return DEFAULT_HOUSE;
}
